import { Tabs, theme } from '@arnold/common';
import styled from '@emotion/styled/macro';
import { prop, sortBy } from 'ramda';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { ChannelBanner } from '../components';
import { ChannelOverviewQuery, Features, OnePlaceAuthInput, UserOrgRole } from '../generated/hooks';
import { PageTitleAndFavicon } from '../utils';
import { CardType, OverviewAnswersCard, OverviewReportCard } from './OverviewCards';
import { OverviewSettings } from './OverviewSettings';

const EmptyMessage = styled.h1`
  margin: 64px 32px;
  color: #bdbdbd;
`;

const Content = styled.div`
  max-width: 960px;
  width: 90vw;
  margin-left: auto;
  margin-right: auto;
  padding: 0 12px 40px;
  flex: 1 0 auto;
  @media (max-width: ${theme.breakpoint.small}) {
    width: 100vw;
  }
`;

export interface OverviewProps {
  respondents: Omit<
    NonNullable<NonNullable<ChannelOverviewQuery['channelOverview']>['respondents']>[0],
    '__typename'
  >[];
  user: Omit<
    NonNullable<NonNullable<ChannelOverviewQuery['channelOverview']>['user']>,
    '__typename' | 'intercomHash' | 'firstname' | 'surname'
  >;
  baseUrl: string;
  auth?: OnePlaceAuthInput;
}

export enum TABS {
  REPORTS = 'reports',
  ANSWERS = 'answers',
  SETTINGS = 'settings',
}

const Overview = ({ respondents, baseUrl, user, auth }: OverviewProps) => {
  const { t } = useTranslation('overview');
  let { tab: selectedTab } = useParams<{ tab?: TABS }>();

  const channels = respondents.map((respondent) => respondent.channels ?? []).flat();

  const bannerChannel =
    sortBy(prop('endsAt'))(
      channels?.filter((channel) => channel.isOpen && !channel.isFinished && !channel.disabled)!,
    )[0] || sortBy(prop('endsAt'))(channels?.filter((channel) => !channel.isFinished && !channel.disabled)!)[0];

  let cards = sortBy(prop('name'))(
    respondents.map((respondent) => ({
      name: respondent.organization.name,
      reportCards: (respondent.reports || [])
        .map((report) => ({
          id: report.accessKey,
          type: report.survey ? CardType.Report : CardType.ReportSequence,
          topicName: report.topicName,
          from: report.survey ? report.survey.sendAt : report.surveyGroup?.from,
          to: report.survey ? report.survey.endsAt : report.surveyGroup?.to,
          opened: !report.unreadMessages?.count,
          unreadMessages: report.unreadMessages?.count,
          reportFrom: report.unreadMessages?.from,
        }))
        .sort(sortReportCards),
      answersCards: (respondent.channels || [])
        .map((channel) => ({
          id: channel.id,
          topicName: channel.topicName,
          answered: channel.isFinishedByRespondent,
          answeredAnonymously: channel.containsOriginalAnonymity,
          from: channel.sendAt,
          to: channel.endsAt,
          reactionsCount: channel.reactions?.length ?? 0,
        }))
        .sort(sortAnswerCards),
    })),
  );

  const reportRecipient =
    respondents.some((r) => r.organizationRole === UserOrgRole.OrgAdmin || r.isLeader || r.isReportRecipient) ||
    cards.some((c) => c.reportCards.length > 0);

  const orgHaveShowSettingsFeature = respondents.some(
    (r) => r.organization.featureNames?.includes(Features.ShowNotificationSettings),
  );

  const availableTabs = [
    reportRecipient && TABS.REPORTS,
    TABS.ANSWERS,
    reportRecipient && orgHaveShowSettingsFeature && TABS.SETTINGS,
  ].filter(Boolean) as TABS[];

  if (!selectedTab || !availableTabs.includes(selectedTab)) {
    selectedTab = availableTabs[0];
  }

  // do not show empty companies with no answers or reports
  cards = cards.filter((card) =>
    selectedTab === TABS.ANSWERS ? card.answersCards.length > 0 : card.reportCards.length > 0,
  );

  return (
    <>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: '#f9f9f9',
          height: '100vh',
          overflow: 'auto',
        }}
        data-cy="one-place-page"
      >
        {bannerChannel && <ChannelBanner channel={bannerChannel} />}
        <Content>
          <PageTitle>{t('myArnold')}</PageTitle>
          <Tabs
            items={availableTabs.map((tab) => ({
              title: t(`tab${capitalizeFirstLetter(tab)}`),
              link: `${baseUrl}/${tab}`,
              active: tab === selectedTab,
              icomTag: `tab-overview-${tab}`,
            }))}
            theme={theme}
            customStyles={{ letterSpacing: '1px' }}
          />
          {selectedTab !== TABS.SETTINGS && (
            <>
              {cards.map((organization, i) => (
                <React.Fragment key={selectedTab! + i}>
                  <OrganizationName>{cards.length > 1 && organization.name}</OrganizationName>
                  {selectedTab === TABS.ANSWERS &&
                    organization.answersCards.map((card) => <OverviewAnswersCard key={card.id} card={card} />)}
                  {selectedTab === TABS.REPORTS &&
                    organization.reportCards.map((card) => <OverviewReportCard key={card.id} card={card} />)}
                </React.Fragment>
              ))}
              {cards.length === 0 && selectedTab === TABS.ANSWERS && (
                <EmptyMessage className="h2">{t('answersEmptyMessage')}</EmptyMessage>
              )}
              {cards.length === 0 && selectedTab === TABS.REPORTS && (
                <EmptyMessage className="h2">{t('reportsEmptyMessage')}</EmptyMessage>
              )}
            </>
          )}
          {selectedTab === TABS.SETTINGS && <OverviewSettings respondents={respondents} user={user} auth={auth} />}
        </Content>
      </div>
      <PageTitleAndFavicon title favicon="arnold" />
    </>
  );
};

export default Overview;

const PageTitle = styled.h1({
  ...theme.typography.heading.xLarge.default,
  fontFamily: theme.typography.heading.xLarge.default.family,
  color: 'black',
  marginTop: theme.spacing.h,
});

const OrganizationName = styled.h2`
  margin-top: ${theme.spacing.h};
`;

/**
 * Sort answer cards by date and answered status
 * The cards that are not answered and to date is in the future should be first
 * Otherwise sort by date descending
 */
const sortAnswerCards = (a: { to: string; answered: boolean }, b: { to: string; answered: boolean }) => {
  const canBeStillAnsweredA = !a.answered && new Date(a.to) > new Date();
  const canBeStillAnsweredB = !b.answered && new Date(b.to) > new Date();
  if (canBeStillAnsweredA && !canBeStillAnsweredB) {
    return -1;
  }
  if (!canBeStillAnsweredA && canBeStillAnsweredB) {
    return 1;
  }
  return new Date(b.to).getTime() - new Date(a.to).getTime();
};

/**
 * Sort report cards by date and opened status
 * The cards that are not opened should be first
 * Otherwise sort by date descending
 */
const sortReportCards = (a: { to: string; opened: boolean }, b: { to: string; opened: boolean }) => {
  if (!a.opened && b.opened) {
    return -1;
  }
  if (a.opened && !b.opened) {
    return 1;
  }
  return new Date(b.to).getTime() - new Date(a.to).getTime();
};

const capitalizeFirstLetter = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);
